import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

import type { Chat } from '@mint-sdk/api-ai-assistant';

// "At the bottom" tolerance, compensates sub-pixel scroll metric rounding.
const BOTTOM_THRESHOLD_PX = 4;
// The settle gate lifts after this long without a content resize…
const SETTLE_QUIET_MS = 200;
// …and never stays up longer than this (a streaming reply resizes forever).
const SETTLE_MAX_MS = 1000;

// jsdom implements scrollTop but not Element#scrollTo, so consumer specs
// would crash on render without the fallback.
const scrollElementTo = (element: HTMLElement, top: number) => {
  if (typeof element.scrollTo === 'function') {
    element.scrollTo({ top });
  } else {
    element.scrollTop = top;
  }
};

type Params = {
  chatId: string;
  /** Scroll container: DemoChat's column on desktop, the Messages root on mobile. */
  containerRef?: RefObject<HTMLDivElement | null>;
  isChatHistoryLoading: boolean;
  isWelcomeMsgVisible: boolean;
  lastMessage?: Chat.ChatMessageRecordType;
};

/**
 * Auto-scroll orchestration for the chat message list.
 *
 * Message content sizes itself asynchronously — AG Grid measures autoHeight
 * rows over several frames, charts and images do the same — so a single
 * scroll-to-bottom on open lands mid-history and then drifts. Three
 * cooperating layers solve this:
 *
 * 1. Settle gate — first open only, while the history skeleton is already on
 *    screen: the content renders invisibly (`isSettling`) until its layout
 *    goes quiet for SETTLE_QUIET_MS (hard-capped by SETTLE_MAX_MS), then the
 *    chat is revealed pre-scrolled to the bottom. Chats served from the
 *    react-query cache skip the gate — flashing a skeleton over instant data
 *    reads as a slowdown; layer 2 absorbs their residual reflow.
 * 2. Bottom pin — while pinned, every content resize re-glues the view to the
 *    bottom (the ResizeObserver fires before paint, so there is no flicker).
 *    Scrolling up unpins, returning to the bottom re-pins. Unpinning requires
 *    scrollTop to have actually decreased: scroll events arrive
 *    asynchronously, so judging by distance-from-bottom alone would mistake
 *    our own scroll-to-bottom (delivered after the content grew again) for
 *    the user scrolling away.
 * 3. Send scroll — a newly sent user message smooth-scrolls into view and
 *    drops the pin so layer 2 doesn't fight the animation; the scroll
 *    listener re-pins once the message lands at the bottom.
 *
 * The container must keep native scroll anchoring disabled
 * ([overflow-anchor:none]) so the browser doesn't fight layer 2. All mount
 * scrolls are either guarded to run once per chat or safe to repeat, which
 * keeps StrictMode's double-run of dev effects harmless.
 */
export const useChatAutoScroll = ({
  chatId,
  containerRef,
  isChatHistoryLoading,
  isWelcomeMsgVisible,
  lastMessage,
}: Params) => {
  const lastMessageId = lastMessage?.messageId ?? null;
  const isLastMessageHuman = lastMessage?.message.type === 'human';

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const autoScrolledChatIdRef = useRef<string | null>(null);
  const isPinnedToBottomRef = useRef(false);
  const lastSeenMessageIdRef = useRef<number | null>(null);
  const contentResizeObserverRef = useRef<ResizeObserver | null>(null);
  const settleQuietTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Gate only first-time opens; without ResizeObserver (jsdom) quiet can't be
  // detected, so the gate is skipped there as well.
  const [isSettling, setIsSettling] = useState(
    () => isChatHistoryLoading && typeof ResizeObserver !== 'undefined',
  );
  const isSettlingRef = useRef(isSettling);

  const scrollToBottom = useCallback((node: HTMLDivElement | null) => {
    requestAnimationFrame(() => {
      node?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
    });
  }, []);

  const scrollToBottomInstant = useCallback(() => {
    const container = containerRef?.current;

    if (container) {
      scrollElementTo(container, container.scrollHeight);
    } else {
      bottomRef.current?.scrollIntoView?.({ behavior: 'auto', block: 'end' });
    }
  }, [containerRef]);

  const finishSettling = useCallback(() => {
    if (!isSettlingRef.current) return;
    isSettlingRef.current = false;

    if (settleQuietTimerRef.current) {
      clearTimeout(settleQuietTimerRef.current);
      settleQuietTimerRef.current = null;
    }
    setIsSettling(false);
  }, []);

  // Content observer: quiet detection while settling, bottom re-glue while
  // pinned. A callback ref, so it follows the content div across remounts.
  const setContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentResizeObserverRef.current?.disconnect();
      contentResizeObserverRef.current = null;

      if (node && typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => {
          if (isSettlingRef.current) {
            if (settleQuietTimerRef.current) {
              clearTimeout(settleQuietTimerRef.current);
            }
            settleQuietTimerRef.current = setTimeout(
              finishSettling,
              SETTLE_QUIET_MS,
            );
            return;
          }

          if (isPinnedToBottomRef.current) {
            scrollToBottomInstant();
          }
        });
        observer.observe(node);
        contentResizeObserverRef.current = observer;
      }
    },
    [finishSettling, scrollToBottomInstant],
  );

  // Hard cap on the gate, counted from the moment the content mounts.
  useEffect(() => {
    if (isChatHistoryLoading || !isSettling) return;

    const timer = setTimeout(finishSettling, SETTLE_MAX_MS);
    return () => clearTimeout(timer);
  }, [isChatHistoryLoading, isSettling, finishSettling]);

  // The container outlives chat switches, so while the gate is up, drop the
  // previous chat's scroll offset to keep the skeleton in view.
  useLayoutEffect(() => {
    if (!isSettling) return;

    const container = containerRef?.current;
    if (container) {
      scrollElementTo(container, 0);
    }
  }, [containerRef, isSettling]);

  // Reveal the chat pre-scrolled to the bottom, once per chat.
  useLayoutEffect(() => {
    if (isChatHistoryLoading || isSettling) return;
    if (autoScrolledChatIdRef.current === chatId) return;
    autoScrolledChatIdRef.current = chatId;

    lastSeenMessageIdRef.current = lastMessageId;
    isPinnedToBottomRef.current = true;
    scrollToBottomInstant();
  }, [
    chatId,
    isChatHistoryLoading,
    isSettling,
    lastMessageId,
    scrollToBottomInstant,
  ]);

  // The container itself can also resize (the chat input mounting, drawer or
  // window resizes) — that moves the bottom without any content change, so
  // re-glue while pinned.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (!isSettlingRef.current && isPinnedToBottomRef.current) {
        scrollToBottomInstant();
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [
    containerRef,
    isChatHistoryLoading,
    isWelcomeMsgVisible,
    scrollToBottomInstant,
  ]);

  // Pin/unpin from the user's own scrolling (see layer 2 above).
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    let previousScrollTop = container.scrollTop;

    const handleScroll = () => {
      const { scrollTop } = container;
      const distanceFromBottom =
        container.scrollHeight - scrollTop - container.clientHeight;
      const hasScrolledUp = scrollTop < previousScrollTop;
      previousScrollTop = scrollTop;

      if (hasScrolledUp && distanceFromBottom > BOTTOM_THRESHOLD_PX) {
        isPinnedToBottomRef.current = false;
      } else if (distanceFromBottom <= BOTTOM_THRESHOLD_PX) {
        isPinnedToBottomRef.current = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef, isChatHistoryLoading, isWelcomeMsgVisible]);

  useEffect(
    () => () => {
      if (settleQuietTimerRef.current) {
        clearTimeout(settleQuietTimerRef.current);
      }
    },
    [],
  );

  // Smooth-scroll to the message the user just sent.
  useEffect(() => {
    if (autoScrolledChatIdRef.current !== chatId) return;
    if (lastSeenMessageIdRef.current === lastMessageId) return;
    lastSeenMessageIdRef.current = lastMessageId;

    if (isLastMessageHuman) {
      isPinnedToBottomRef.current = false;
      lastUserMessageRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [chatId, lastMessageId, isLastMessageHuman]);

  return {
    isSettling,
    bottomRef,
    lastUserMessageRef,
    setContentRef,
    scrollToBottom,
  };
};
